import type { AxiosResponse } from 'axios'
import { httpClient } from '@/shared/api/http-client'
import type {
  AuthTokenResponse,
  LoginPayload,
  RegisterPayload,
  ResetPasswordPayload,
  UserResponse,
  AuthUser,
  GuestStartPayload,
  GuestStartResponse,
  GuestConvertPayload,
  GuestConvertResponse,
} from '@/features/auth/types'
import type { AiModel } from '@/shared/types/ai-model'

export async function login(payload: LoginPayload) {
  const requestBody = {
    username: payload.username,
    password: payload.password,
  }

  const response = await httpClient.post<AuthTokenResponse, AxiosResponse<AuthTokenResponse>>(
    '/api/Auth/token',
    requestBody,
  )
  return response.data
}

export async function register(payload: RegisterPayload): Promise<void> {
  await httpClient.post('/api/Auth/user', payload)
}

export async function startGuest(payload: GuestStartPayload): Promise<{ token: string; user: AuthUser; chat: GuestStartResponse['chat'] }> {
  const response = await httpClient.post<GuestStartResponse, AxiosResponse<GuestStartResponse>>(
    '/api/Auth/guest/start',
    payload,
  )
  return {
    token: response.data.token,
    user: mapUserDto(response.data.user, false),
    chat: response.data.chat,
  }
}

export async function convertGuest(payload: GuestConvertPayload): Promise<{ token: string; user: AuthUser }> {
  const response = await httpClient.post<GuestConvertResponse, AxiosResponse<GuestConvertResponse>>(
    '/api/Auth/guest/convert',
    payload,
  )
  return {
    token: response.data.token,
    user: mapUserDto(response.data.user, false),
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  await httpClient.post('/api/Auth/forgot-password', { email })
}

export async function submitPasswordReset(payload: ResetPasswordPayload): Promise<void> {
  await httpClient.post('/api/Auth/reset-password', payload)
}

export async function fetchCurrentUser(): Promise<AuthUser> {
  const response = await httpClient.get<UserResponse, AxiosResponse<UserResponse>>('/api/User/user')
  return mapUserResponse(response.data)
}

export async function logout(): Promise<void> {
  await httpClient.post('/api/Auth/logout')
}

export function mapUserResponse(response: UserResponse): AuthUser {
  return mapUserDto(response.user, response.tokensAwardedToday)
}

function mapUserDto(user: UserResponse['user'], tokensAwardedToday?: boolean): AuthUser {
  const chatSummaries = user.chatSummaries?.map((summary) => ({
    chatId: summary.chatId,
    chatName: summary.chatName ?? summary.name ?? `Chat #${summary.chatId}`,
    modelId: summary.modelId,
    isRoleplay: summary.isRoleplay ?? false,
    lastMessage: summary.lastMessage ?? summary.lastMessageText,
    updatedAt: summary.updatedAt ?? summary.lastMessageTimestamp,
  }))

  return {
    id: user.id,
    username: user.userName,
    email: user.email,
    telegramUsername: user.telegramUsername,
    premiumUntil: user.premiumUntil ?? null,
    tokensBalance: user.tokensBalance,
    chatSummaries,
    tokensAwardedToday,
    preferredLanguage: normalizeLanguage(user.preferredLanguage),
    preferredAiModel: normalizeAiModel(user.preferredAiModel),
    memoryPreferences: user.memoryPreferences ?? null,
    isGuest: user.isGuest ?? false,
    guestCreatedAt: user.guestCreatedAt ?? null,
    guestExpiresAt: user.guestExpiresAt ?? null,
    guestConvertedAt: user.guestConvertedAt ?? null,
    guestTextMessagesUsed: user.guestTextMessagesUsed ?? 0,
    guestImageRequestsUsed: user.guestImageRequestsUsed ?? 0,
    guestLimits: user.guestLimits ?? null,
  }
}

function normalizeLanguage(lang?: string | null) {
  if (!lang) return null
  const normalized = lang.trim().toLowerCase()
  if (normalized.startsWith('ru')) return 'ru'
  if (normalized.startsWith('en')) return 'en'
  return normalized || null
}

function normalizeAiModel(model?: AiModel | null): AiModel | null {
  if (!model) return null
  if (model === 'gpt' || model === 'llama' || model === 'balanced') {
    return model
  }
  return null
}

